import * as PIXI from 'pixi.js-legacy';

import bracketsMixin from './bracketsMixin';
import Item from '../Item';
import constants from '../../settings/constants';
import getDepthOffset from '../util/getDepthOffset';
import { config as asConfig } from './AdjustableConfig';
import productService from '../../services/products';
import project from '../util/project';
import tacHelpers from '../../state/tac/tacHelpers';
import geometry from '../util/geometry';
import adjustableContainer from '../util/adjustableContainer';

export default class Table extends bracketsMixin(Item) {
  update(args) {
    this.localLegItem = this.localLegItem || {
      ...productService.getProduct(asConfig.legs.topId),
    };

    super.update(args);

    if (this.parentItem?.itemid) {
      const { item, parentItem } = this;
      const rects = tacHelpers.getRawRects(this.app.tac);
      const gItem = {
        ...item,
        x: item.x + parentItem.x,
        y: item.y + parentItem.y,
        z: item.z + parentItem.z,
      };

      const collidingRects = geometry.getCollidingRects(gItem, rects);
      const collidingTableSections = collidingRects.filter(
        rect =>
          productService.isSection(rect) &&
          rect.itemid !== parentItem.itemid &&
          productService.fitsTable(rect)
      );

      this.forceClosedDropAreas = this.forceClosedDropAreas || [];
      this.forceClosedDropAreas.forEach(dropArea => {
        // only force-show dropAreas that are not on our current parent
        if (dropArea.parent?.parent?.item?.itemid !== parentItem.itemid) {
          dropArea.visible = true;
        }
      });
      this.forceClosedDropAreas = [];

      const gSpriteBounds = {
        ...this.getGlobalPosition(),
        height: this.data.faceSize.height * this.ratio,
        width: this.sprite.width,
      };

      collidingTableSections.forEach(section => {
        const sprite = this.app.getSprite(section.itemid);

        if (sprite) {
          sprite.dropAreas.children
            .filter(dropArea => {
              return geometry.collides(
                {
                  ...dropArea.getGlobalPosition(),
                  height: dropArea.height,
                  width: dropArea.width,
                },
                gSpriteBounds
              );
            })
            .forEach(dropArea => {
              dropArea.visible = false;
              this.forceClosedDropAreas.push(dropArea);
            });
        }
      });
    }
  }

  getSelfHitArea() {
    const { scaledVertices } = this.data;
    const topPadding = constants.DISTANCE_BETWEEN_ATTACHMENTS * this.ratio;
    const bottomPadding = topPadding / 2;
    const vertices = [
      [scaledVertices[0][0], scaledVertices[0][1] + bottomPadding],
      [scaledVertices[3][0], scaledVertices[3][1]],
      [scaledVertices[7][0], scaledVertices[7][1] - topPadding],
      [scaledVertices[6][0], scaledVertices[6][1] - topPadding],
      [scaledVertices[5][0], scaledVertices[5][1]],
      [scaledVertices[1][0], scaledVertices[1][1] + bottomPadding],
    ];

    return {
      regions: [vertices],
    };
  }

  draw() {
    const { scaledVertices } = this.data;

    const depthOffset = getDepthOffset({ z: this.localLegItem.depth });
    this.localLegItem.height = Math.min(
      Math.max(this.item?.y + this.parentItem?.y, asConfig.legs.minHeight),
      asConfig.legs.maxHeight
    );

    this.legsContainer = this.legsContainer || new PIXI.Container();
    this.legsContainer.x = scaledVertices[0][0] + depthOffset.x * this.ratio;
    this.legsContainer.y = scaledVertices[0][1] - depthOffset.y * this.ratio;

    if (!this.legsContainer.parent) {
      this.addChild(this.legsContainer);
    }

    const legOuterSize = project.outer(this.localLegItem);

    this.leftLegContainer = adjustableContainer(
      this.leftLegContainer,
      legOuterSize,
      asConfig.legs,
      this.ratio
    );

    if (!this.leftLegContainer.parent) {
      this.legsContainer.addChild(this.leftLegContainer);
    }

    this.rightLegContainer = adjustableContainer(
      this.rightLegContainer,
      legOuterSize,
      asConfig.legs,
      this.ratio
    );

    this.rightLegContainer.x =
      scaledVertices[1][0] -
      this.rightLegContainer.width -
      depthOffset.x * this.ratio;

    if (!this.rightLegContainer.parent) {
      this.legsContainer.addChild(this.rightLegContainer);
    }

    super.draw();
  }
}
